import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import { Check } from '@mui/icons-material';
import LanguageIcon from '@mui/icons-material/Language';
import { useDispatch, useSelector } from 'react-redux';
import { getLanguageConfig, LANGUAGE_OPTIONS } from '../constants/appConstants';
import { selectLanguage, setLanguage } from '../api/ui/languageSlice';

/**
 * Menu that sets the UI language (English, Arabic, Simplified Chinese).
 * The button shows the current language in its native name.
 */
const LanguageSelect = ({ sx, size = 'small' }) => {
  const dispatch = useDispatch();
  const language = useSelector(selectLanguage);
  const [anchorEl, setAnchorEl] = useState(null);
  const current = LANGUAGE_OPTIONS.find((option) => option.code === language) ?? LANGUAGE_OPTIONS[0];

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (code) => {
    dispatch(setLanguage(code));
    handleClose();
  };

  return (
    <>
      <Button
        type="button"
        size={size}
        onClick={handleOpen}
        startIcon={<LanguageIcon />}
        color="inherit"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-label={`Language: ${current.nativeLabel}`}
        sx={sx}
      >
        {current.nativeLabel}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {LANGUAGE_OPTIONS.map((option) => {
          const selected = option.code === language;

          return (
            <MenuItem
              key={option.code}
              selected={selected}
              lang={getLanguageConfig(option.code).htmlLang}
              onClick={() => handleSelect(option.code)}
              sx={{ gap: 1.5, alignItems: 'center', minWidth: 220 }}
            >
              <Box component="span" sx={{ width: 28, flexShrink: 0, color: 'text.secondary', fontSize: '0.75rem' }}>
                {option.codeLabel}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box component="span" sx={{ display: 'block', lineHeight: 1.2 }}>
                  {option.nativeLabel}
                </Box>
                {option.englishLabel !== option.nativeLabel && (
                  <Box
                    component="span"
                    sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.2 }}
                  >
                    {option.englishLabel}
                  </Box>
                )}
              </Box>
              {selected && <Check sx={{ color: 'primary.main', fontSize: 18 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default LanguageSelect;
